import { Dispatch, SetStateAction, useCallback } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import { Options } from './Options';
import { Selected } from './Selected';
import { styles } from './styles';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { Card } from '@/components/ui/therapist/Card';
import { useContinueToSetDetails } from '@/hooks/useContinueToSetDetails';
import { SetType, Template } from '@/components/Template/types';
import { TextItem } from '@/contexts/TextSetContext/types';

type DataKeyName = 'words' | 'sentences' | 'texts';

type DynamicName = 'wordset' | 'sentenceset' | 'textset';

type SetDataItem = {
  _id: string;
  textLength?: TextItem['textLength'];
};

type SetsSearchData<TItem extends SetDataItem = SetDataItem> = {
  totalCount: number;
} & Partial<Record<DataKeyName, TItem[]>>;

type SetEditData = { name?: string } & Record<string, unknown>;

type SelectedSetsDataProps<TItem extends SetDataItem> = {
  data?: SetsSearchData<TItem>;
  selectedData?: TItem[];
  changeSelectedData?: Dispatch<SetStateAction<TItem[]>>;
  patientId?: string;
  page: number;
  changePage?: (value: number, arrowName?: null) => void;
  limitModal?: boolean;
  changeLimitModal?: () => void;
  setId?: string;
  editData?: SetEditData | null;
  dataKeyName: DataKeyName;
  apiKeyName: SetType;
  dynamicName: DynamicName;
  isTextSet?: boolean;
  textLength?: TextItem['textLength'];
  template?: Template | null;
};

const optionsTitle: Record<SetType, string> = {
  wordsets: 'Wörter aus Suche ',
  sentencesets: 'Sätze aus Suche ',
  textsets: 'Texte aus Suche ',
};

const selectedTitle: Record<SetType, string> = {
  wordsets: 'Neues Wörterset',
  sentencesets: 'Neues Sätzeset',
  textsets: 'Neues Texteset',
};

const emptyTexts: Record<SetType, string> = {
  wordsets: 'Keine Wörter mit diesen Eigenschaften gefunden.',
  sentencesets: 'Keine Sätze mit diesen Eigenschaften gefunden.',
  textsets: 'Keine Texte mit diesen Eigenschaften gefunden.',
};

function isPresent<T>(value: T | undefined): value is T {
  return value !== undefined;
}

export function SelectedSetsData<TItem extends SetDataItem>({
  data = { totalCount: 0 },
  selectedData = [],
  changeSelectedData = () => {},
  patientId,
  page,
  changePage = () => {},
  limitModal = false,
  changeLimitModal = () => {},
  setId,
  editData,
  dataKeyName,
  apiKeyName,
  dynamicName,
  isTextSet,
  textLength,
  template,
}: SelectedSetsDataProps<TItem>) {
  const items = data[dataKeyName] ?? [];

  const { continueToDetails } = useContinueToSetDetails({
    apiKeyName,
    items: selectedData,
    dataKeyName,
    dynamicName,
    patientId,
    setId,
    editData,
    template,
  });

  const onOptionClick = useCallback(
    (newData: TItem, isSelected: boolean, isText: boolean) => {
      if (isText) {
        if (!isSelected) {
          changeSelectedData((prevState) => {
            const newDatas = [...prevState, newData];
            if (newDatas.length > 10) {
              changeLimitModal();
              return prevState;
            } else {
              const prevStateIds = prevState.map((prev) => prev._id);
              if (prevStateIds.includes(newData._id)) {
                const clonedState = [...prevState];
                const foundIndex = clonedState.findIndex(
                  (value) => value._id == newData._id,
                );
                if (foundIndex >= 0) {
                  clonedState[foundIndex] = newData;
                  return clonedState;
                } else {
                  return [...prevState, newData];
                }
              } else {
                return [...prevState, newData];
              }
            }
          });
        } else {
          changeSelectedData((prevState) => {
            const filteredState = [...prevState].filter((eachData) => {
              if (eachData._id !== newData._id) {
                return true;
              } else {
                if (
                  eachData._id === newData._id &&
                  eachData.textLength === newData.textLength
                ) {
                  return false;
                } else {
                  return true;
                }
              }
            });
            return filteredState;
          });
        }
      } else {
        if (!isSelected) {
          changeSelectedData((prevState) => {
            const newDatas = [...prevState, newData];
            if (newDatas.length > 50) {
              changeLimitModal();
              return prevState;
            } else {
              return [...prevState, newData];
            }
          });
        } else {
          changeSelectedData((prevState) => {
            const filteredState = [...prevState].filter((eachData) => {
              return eachData._id !== newData._id;
            });
            return filteredState;
          });
        }
      }
    },
    [],
  );

  const onXClick = useCallback((newWord: TItem, isText: boolean) => {
    onOptionClick(newWord, true, isText);
  }, []);

  const selectAllWords = useCallback(() => {
    changeSelectedData((prevState) => {
      if (isTextSet) {
        const includedIds: Record<string, number> = {};
        const mergedStates = [...prevState, ...items];
        const mergedStatesJSON = mergedStates.map(
          (eachVal) => eachVal._id + 'x' + eachVal.textLength,
        );
        const set = new Set([...mergedStatesJSON]);
        const unduplicatedState = Array.from(set)
          .map((eachVal) =>
            mergedStates.find(
              (obj) =>
                obj._id === eachVal.split('x')[0] &&
                obj.textLength === eachVal.split('x')[1],
            ),
          )
          .filter(isPresent);

        unduplicatedState.forEach((eachOne) => {
          if (Object.keys(includedIds).includes(eachOne._id)) {
            includedIds[eachOne._id] = includedIds[eachOne._id] + 1;
          } else {
            includedIds[eachOne._id] = 1;
          }
        });

        let newAlteredState = unduplicatedState;
        Object.keys(includedIds).forEach((key) => {
          const value = includedIds[key];
          if (value > 1) {
            newAlteredState = newAlteredState.filter((eachOne) => {
              if (eachOne._id != key) {
                return true;
              } else {
                if (eachOne.textLength == textLength) {
                  return true;
                } else {
                  return false;
                }
              }
            });
          }
        });

        if (newAlteredState.length > 10) {
          changeLimitModal();
          for (let i = newAlteredState.length; i > 10; i--) {
            newAlteredState.pop();
          }
          return newAlteredState;
        } else {
          return newAlteredState;
        }
      } else {
        const mergedStates = [...prevState, ...items];
        const mergedStatesJSON = mergedStates.map((eachVal) => eachVal._id);
        const set = new Set([...mergedStatesJSON]);
        const unduplicatedState = Array.from(set)
          .map((eachVal) => mergedStates.find((obj) => obj._id === eachVal))
          .filter(isPresent);
        if (unduplicatedState.length > 50) {
          changeLimitModal();
          const newState = [...prevState];
          for (let i = 0; i < items.length; i++) {
            const newData = items[i];
            if (newState.length === 50) {
              break;
            }
            newState.push(newData);
          }
          return newState;
        } else {
          return unduplicatedState;
        }
      }
    });
  }, [items]);

  const clearAllselectedData = useCallback(() => {
    changeSelectedData([]);
  }, []);

  const handleButtonPress = useCallback(() => {
    continueToDetails();
  }, [continueToDetails]);

  const maxPage = Math.ceil(data.totalCount / 30);

  const handlePageChange = useCallback((arrowName: null = null) => {
    return (value: number) => {
      changePage(value, arrowName);
    };
  }, []);

  return (
    <View style={styles.container}>
      <Card style={styles.card}>
        <Options
          title={optionsTitle[apiKeyName]}
          data={items}
          isEmptyText={emptyTexts[apiKeyName]}
          selectedData={selectedData.map((eachData) => eachData._id)}
          pureSelectedData={selectedData}
          dataCount={data.totalCount}
          onPress={onOptionClick}
          headerPress={selectAllWords}
          page={page}
          maxPage={maxPage}
          handlePageChange={handlePageChange}
          selectedLimit={
            isTextSet ? selectedData.length === 10 : selectedData.length === 50
          }
          isTextSet={isTextSet}
          textLength={textLength}
        />
      </Card>
      <Card style={styles.card}>
        <Selected
          title={(editData?.name || selectedTitle[apiKeyName]) + ' '}
          data={selectedData}
          dataCount={selectedData.length}
          onPress={onXClick}
          headerPress={clearAllselectedData}
          onButtonPress={handleButtonPress}
          isTextSet={isTextSet}
        />
      </Card>
      <Modal
        isVisible={limitModal}
        confirmActionButton={
          <Button label="Ok" onPress={() => changeLimitModal()} />
        }
        title="Limit erreicht"
        onClose={() => changeLimitModal()}
      >
        <Text style={{ fontSize: 20 }}>
          {isTextSet
            ? 'Das Übungsset hat ein Limit von 10 Items.'
            : 'Das Übungsset hat ein Limit von 50 Items.'}
        </Text>
      </Modal>
    </View>
  );
}
